import React from 'react';
import { groupBy, pick, keys, head, isNumber, values } from 'lodash';
import { useLocale } from 'locale';
import { ILocalKindergarten } from 'utils/entities';
import { H3 } from 'ds/components/typography';
import { SchoolsTable } from '../components/SchoolsTable';
import { Kindergartens } from '../components/Kindergartens';
import styled from '@emotion/styled';
import { SectionHeader, TableHeader } from '../styled';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { ILSchoolGroupsSummary } from 'store/state/selectors/insights/schools/types';


export const Root = styled.div`
  ${({ theme }) => `
    margin-top: ${theme.spacing(7)};
  `}
`;

const ScoreWrapper = styled.div`
  ${({ theme }) => `
    margin-bottom: ${theme.spacing(5)};
  `}
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
  `}
`;

interface SchoolsSectionProps {
  schoolData: ILSchoolGroupsSummary;
  kindergartenInArea: ILocalKindergarten[];
  areaCityName?: string;
  areaNeighbourhoodName?: string;
}

export const SchoolsSection: React.FC<SchoolsSectionProps> = ({
  kindergartenInArea,
  schoolData,
  areaCityName = null,
  areaNeighbourhoodName = null,
}) => {
  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const isMobile = screenBreakpoint === 1;
  const hasKindergarten = kindergartenInArea && kindergartenInArea.length > 0;

  const elementarySchools = schoolData && schoolData.ELEMENTARY ? schoolData.ELEMENTARY : [];
  const highSchools = schoolData && schoolData.HIGHSCHOOL ? schoolData.HIGHSCHOOL : [];

  const schoolsInArea = Boolean(elementarySchools.length || highSchools.length);
  const isCity = Boolean(areaCityName);

  const extraData = React.useMemo(() => {
    const keysToPick = [ isCity ? null : 'neighborhoodMadadMadlan', 'madlanEducationIndexCityAverage', 'madlanEducationIndexNationalAverage' ].filter(Boolean);
    const map = groupBy(highSchools, (s) => values(pick(s, keysToPick)).length);
    const highKey = Math.max(...keys(map).map(Number));
    return highSchools.length && isNumber(highKey) ? pick(head(map[highKey]), keysToPick) : undefined;
  }, [ highSchools, isCity ]);

  if (!schoolsInArea && !hasKindergarten) return null;

  return (
    <Root>
      {schoolsInArea ? (
        <>
          <SectionHeader data-auto="schools-section">
            <H3 weight="bold">{t('localPage.schools.title', { areaName: areaCityName || areaNeighbourhoodName })}</H3>
          </SectionHeader>
          <ScoreWrapper>
            {elementarySchools.length ? (
              <>
                <TableHeader weight="bold">
                  {t('localPage.schools.elementarySchools', { isCity })}
                </TableHeader>
                <SchoolsTable showLess={isMobile} data={elementarySchools} />
              </>
            ) : null}
            {highSchools.length ? (
              <>
                <TableHeader weight="bold">
                  {t('localPage.schools.highSchools', { isCity })}
                </TableHeader>
                <SchoolsTable showLess={isMobile} extraData={extraData} isCity={isCity} data={highSchools} />
              </>
            ) : null}
          </ScoreWrapper>
        </>
      ) : null}
      {hasKindergarten ? (
        <Kindergartens data={kindergartenInArea} areaName={areaNeighbourhoodName} />
      ) : null}
    </Root>
  );
};
